import { DEFAULT_SHELF, preferencesResponse, type AppearancePreference, type Preferences, type ShelfPreference } from '@bookclub/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useAppearance, type Appearance } from '@/theme';
import { apiGet, apiPut } from './client';

// The appearance follows your account: the device keeps its own copy (so the app starts in your style
// before it can ask the server), and the account's choice wins once you're signed in.

/** A change this device couldn't send yet (offline); it wins over the server's until it's sent. */
let unsent: AppearancePreference | null = null;

const same = (a: Appearance, b: AppearancePreference) => a.style === b.style && a.mode === b.mode;

/** The cached preferences with a new appearance, leaving the rest as it is. */
const withAppearance = (appearance: AppearancePreference) => (old: Preferences | undefined): Preferences => ({ shelf: DEFAULT_SHELF, ...old, appearance });

export function usePreferences() {
  return useQuery({
    queryKey: ['preferences'],
    queryFn: async ({ signal }) => apiGet('/api/preferences', preferencesResponse, signal),
  });
}

async function send(appearance: AppearancePreference) {
  try {
    await apiPut('/api/preferences', { appearance }, preferencesResponse);
    unsent = null;
  } catch {
    unsent = appearance;
  }
}

/** Change the appearance here and on every device you're signed in on. */
export function useChangeAppearance() {
  const { appearance, setAppearance } = useAppearance();
  const queryClient = useQueryClient();
  return (change: Partial<Appearance>) => {
    const next = { ...appearance, ...change };
    setAppearance(next);
    queryClient.setQueryData(['preferences'], withAppearance(next));
    void send(next);
  };
}

/**
 * Signed in: take the account's appearance (it changes live when another device changes it). An account
 * without one yet takes this device's; a change made offline is sent first.
 */
export function useAppearanceSync() {
  const queryClient = useQueryClient();
  const { appearance, setAppearance } = useAppearance();
  const prefs = usePreferences();
  const remote = prefs.isSuccess ? prefs.data.appearance : undefined;

  useEffect(() => {
    if (remote === undefined) return;
    if (unsent) {
      void send(unsent);
      return;
    }
    if (remote === null) {
      queryClient.setQueryData(['preferences'], withAppearance(appearance));
      void send(appearance);
    } else if (!same(appearance, remote)) {
      setAppearance(remote);
    }
  }, [remote, appearance, setAppearance, queryClient]);
}

/** Your bookshelf (its finish, and covers or spines), and a way to change it here and on your other devices. */
export function useShelf() {
  const queryClient = useQueryClient();
  const shelf = usePreferences().data?.shelf ?? DEFAULT_SHELF;
  const change = (update: Partial<ShelfPreference>) => {
    queryClient.setQueryData<Preferences>(['preferences'], (old) => ({ appearance: null, ...old, shelf: { ...DEFAULT_SHELF, ...old?.shelf, ...update } }));
    // Not sent (offline, say): the next fetch puts back what the account has.
    apiPut('/api/preferences', { shelf: update }, preferencesResponse).catch(() => queryClient.invalidateQueries({ queryKey: ['preferences'] }));
  };
  return { shelf, change };
}
